import { useCometState } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { normalizeComposerSubmissionContext } from '#pplx/frontend/apps/ask/composer-ui/composerSubmissionContext.js';
import { useComposerSubmitControls } from '#pplx/frontend/apps/ask/composer-ui/useComposerSubmitControls.js';
import { resolveUploadAwareSubmitDisabled, useSubmitAfterUploads, } from '#pplx/frontend/apps/ask/composer-ui/useSubmitAfterUploads.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { usePromptHistoryPlugin } from '#pplx/frontend/composer-sdk-plugins/prompt-history/usePromptHistoryPlugin.js';
import { createMarkdownLinksComposerValue } from '#pplx/frontend/composer-sdk/createMarkdownLinksComposerValue.js';
import { useCallback, useMemo, useState } from 'react';
/** Holds submissions until uploads finish and accepts history only after actual dispatch. */
export function useComposerUploadSubmission({ analyticsSurface, attachments: { attachmentUrls, fileAttachments, hasUploadFailure, isUploading, reset: resetAttachments, resolvedSearchMode, submitUploads, }, compareModelCount, composerPromptHistory, disableSubmission, disabled, getSubmissionMentions, isSubmissionBlocked, isFollowUp, onSubmit, querySource, sendWithoutFailedUploads = false, showStopButton, skipSearchModeGate, submissionRevision, }) {
    const { activeTab, attachedTabs, actions: { clearAttachedTabs }, } = useCometState();
    const history = composerPromptHistory?.history;
    const serializedHistory = useMemo(() => history?.map(createMarkdownLinksComposerValue) ?? [], [history]);
    const { acceptSubmission, plugin: promptHistoryPlugin } = usePromptHistoryPlugin({
        history: serializedHistory,
        disabled: composerPromptHistory === undefined,
        resetKey: composerPromptHistory?.resetKey,
        onEvent: composerPromptHistory?.onEvent,
    });
    const { isModelCouncilSubmitDisabled, onBeforeComposerSubmit } = useComposerSubmitControls({
        compareModelCount,
        hasAttachments: fileAttachments.length > 0,
        isFollowUp,
        querySource,
        selectedSearchMode: resolvedSearchMode,
        showStopButton,
        skipSearchModeGate,
    });
    const resolveSubmission = useCallback((submission, { fromQueue }) => {
        const { mentions, prefills, slashCommand, slashCommandSubmittedLabel } = normalizeComposerSubmissionContext({
            routeContext: { mentions: getSubmissionMentions?.(submission) },
            serializedEditorState: submission.state,
            browserTabs: { activeTab, attachedTabs },
        });
        return {
            attachmentUrls,
            fileAttachments,
            // An empty snapshot must override stale tab mentions in the footer's editor fallback.
            mentions: mentions ?? [],
            prefills,
            slashCommand,
            slashCommandSubmittedLabel,
            submission: {
                ...submission,
                ...(submission.attempt && {
                    attempt: { ...submission.attempt, fromQueue },
                }),
            },
        };
    }, [activeTab, attachedTabs, attachmentUrls, fileAttachments, getSubmissionMentions]);
    const handleDispatchedSubmit = useCallback(({ attachmentUrls: latestAttachmentUrls, fileAttachments: latestFileAttachments, submission, ...semantics }) => {
        const result = onSubmit(submission, {
            attachmentUrls: latestAttachmentUrls,
            fileAttachments: latestFileAttachments,
            ...semantics,
        });
        if (result !== false) {
            acceptSubmission(submission.state);
            clearAttachedTabs();
            resetAttachments();
        }
        return result;
    }, [acceptSubmission, clearAttachedTabs, onSubmit, resetAttachments]);
    // Removing an existing tab narrows the request. Adding or navigating a tab changes its
    // context, so it requires a new submit gesture after uploads.
    const submissionResourceIds = useMemo(() => attachedTabs.map((tab) => `tab:${tab.id}:${String(activeTab.tabId) === tab.id ? activeTab.url : tab.url}`), [activeTab, attachedTabs]);
    const isDisabled = disabled === true;
    const [cancellationVersion, setCancellationVersion] = useState(0);
    const [wasDisabled, setWasDisabled] = useState(isDisabled);
    if (wasDisabled !== isDisabled) {
        setWasDisabled(isDisabled);
        if (isDisabled)
            setCancellationVersion((version) => version + 1);
    }
    const sendableUploads = useMemo(() => sendWithoutFailedUploads
        ? submitUploads.filter((upload) => upload.status !== 'failed')
        : submitUploads, [sendWithoutFailedUploads, submitUploads]);
    const submit = useSubmitAfterUploads({
        analyticsSurface,
        cancellationVersion,
        hasUploadFailure: hasUploadFailure && !sendWithoutFailedUploads,
        isSubmissionBlocked: isModelCouncilSubmitDisabled || isSubmissionBlocked,
        isUploadPending: isUploading,
        onSubmit: handleDispatchedSubmit,
        resolveSubmission,
        submissionResourceIds,
        submissionRevision,
        uploads: sendableUploads,
    });
    const isSubmitDisabled = resolveUploadAwareSubmitDisabled({
        isDisabled: disableSubmission === true || isModelCouncilSubmitDisabled,
        isSubmitBlocked: submit.isSubmitBlocked,
        isSubmitQueued: submit.isSubmitQueued,
    });
    const { requestSubmit: requestSubmitAfterUploads } = submit;
    const requestSubmit = useCallback((submission) => requestSubmitAfterUploads({
        ...submission,
        attempt: {
            method: submission.attempt?.method ?? 'unknown',
            attemptUUID: safeRandomUUID(),
            fromQueue: false,
        },
    }), [requestSubmitAfterUploads]);
    const handleComposerSubmit = useCallback((submission) => onBeforeComposerSubmit(submission)
        ? requestSubmit({
            ...submission,
            attempt: {
                method: submission.keyEvent === undefined ? 'pointer' : 'keyboard',
                fromQueue: false,
            },
        })
        : false, [onBeforeComposerSubmit, requestSubmit]);
    return {
        ...submit,
        requestSubmit,
        isSubmitDisabled,
        onSubmit: handleComposerSubmit,
        promptHistoryPlugin: composerPromptHistory === undefined ? undefined : promptHistoryPlugin,
    };
}
//# sourceMappingURL=useComposerUploadSubmission.js.map